import { DEFAULT_CAMERA_MODE, DEFAULT_JUMP_SUBSTITUTE } from '../../platform/config/camera-mode';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { PlayerId } from '../../shared/types/ids';
import type { ThriveInputEvent } from '../../shared/types/input-events';
import type { GameplayTunables } from '../../shared/types/tunables';
import type { SemanticEventBus } from '../events/semantic-event-bus';
import { HERO_1_PLAYER_ID } from '../keyboard/keyboard-adapter';
import type { CameraMode, JumpSubstitute } from '../../shared/types/camera-mode';
import type { CountAlternationMemo } from '../pose/detectors';
import { createObservationContinuity } from '../pose/observation-continuity';
import {
  createCountAlternationMemo,
  createCountSubstituteDetector,
  createCrouchDetector,
  createHandsDetector,
  createJumpDetector,
  createPresenceDetector,
  createStillnessDetector,
  createZoneDetector,
} from '../pose/detectors';
import type {
  DetectorCapability,
  DetectorOutcome,
  DetectedInput,
  PoseDetector,
} from '../pose/detector-types';
import type { PoseCalibrationThresholds } from '../pose/pose-calibration';
import type { PoseFrame } from '../pose';

/**
 * Camera input adapter (CAM-03/CAM-04, recon §B — the parity contract).
 *
 * It is the camera's counterpart to `keyboard-adapter.ts`, and deliberately reads like it: the
 * whole bet of the platform is that a child answering with their body and a child answering with a
 * keyboard reach the mission through one code path, so this file emits the same ten events, with
 * the same latching, the same retraction discipline and the same `GameplayTunables` timings. If a
 * rule here diverges from the keyboard's, the divergence is the bug.
 *
 * **What it adds, and only because a body is not a keyboard:**
 *
 *   - *The confidence gate* (D-059 PS3-1). A detector that is not sure produces no event at all;
 *     the adapter records input uncertainty instead. An emitted onset therefore *is* a confident
 *     observation, which is the locked "evidence only for confidently observed responses" rule made
 *     structural rather than remembered. A camera that cannot see is never a child who was wrong.
 *   - *Presence is real*. The keyboard announces a player on attach because a keyboard player is
 *     always there; here nobody is in front of the camera until the presence detector says so.
 *   - *Tracking loss retracts* exactly as a keyboard blur does (recon §B.4): held postures are
 *     released under their own meaning, the lane is un-latched, and only then is absence reported.
 *     Never an onset — a child losing tracking mid-crouch must not be read as raising their hands.
 *
 * **What it deliberately does not have:** a freeze gate (D-059 PS3-4 — freeze is a keyboard
 * simulation for Phase 3, and the ordinary pause already covers a grown-up's needs), any knowledge
 * of missions, rounds or answers, and any access to storage. It never sees a camera either: frames
 * arrive from the tracker, and landmarks stop at the detectors.
 *
 * One adapter serves one child. Cooperative camera play is Phase 4 (CAM-07), and nothing here
 * assumes a second body will never exist — it simply speaks for the hero it was given.
 */

/** What the adapter needs of a {@link PoseTracker}: frames, and a way to stop listening. */
export interface PoseFrameSource {
  subscribe(listener: (frame: PoseFrame) => void): () => void;
}

/**
 * The uncertainty seam (D-021, given its first producer here).
 *
 * Structurally the `SessionAnalyticsWriter` method, named locally so the interaction layer does not
 * depend on the mission SDK for one call: what the adapter needs is somewhere to report that it
 * could not see, not a mission.
 */
export interface InputUncertaintyRecorder {
  recordInputUncertainty(
    playerId: PlayerId,
    detail: { capability: string; confidence: number },
  ): void;
}

/**
 * What the centre of the body currently means, mirroring `KeyboardInputContext` exactly.
 *
 * `ZONES` — three-lane mechanics: a crouch is a child looking at the floor, not an answer.
 * `BINARY` — yes/no posture mechanics: a crouch is "no", and there is no centre-lane answer,
 * because on the keyboard the centre key *is* the crouch key (D-035).
 */
export type PoseInputContext = 'ZONES' | 'BINARY';

export const DEFAULT_POSE_INPUT_CONTEXT: PoseInputContext = 'ZONES';

/**
 * What "the camera could not read that" can be about (D-076(1)).
 *
 * Phase 3 had one axis: a detector that could not see well enough. Phase 4 adds the other one PS3-1
 * never had to answer — a reading that is confidently a movement while being ambiguously *whose*
 * movement it is (recon §B.5). It joins the same seam rather than getting a parallel one, because
 * what a session needs to record is identical: the camera could not confidently observe this child,
 * so nothing about them was learned and nothing about them was wrong.
 *
 * `'identity'` is deliberately **not** a {@link DetectorCapability}: no detector reports it and no
 * detector could. Only the lane resolver knows, and only the adapter is told.
 */
export type InputUncertaintyCapability = DetectorCapability | 'identity';

/** The capability an unreadable identity is recorded under. */
export const IDENTITY_CAPABILITY = 'identity';

export interface CameraInputAdapterOptions {
  bus: SemanticEventBus;
  /** Which hero this adapter speaks for. Single-player for Phase 3. */
  playerId?: PlayerId;
  /** Where frames come from. Optional so the adapter can be driven frame by frame in tests. */
  tracker?: PoseFrameSource | null;
  /** Where "I could not see that" goes. Absent is legitimate: nothing is recorded. */
  analytics?: InputUncertaintyRecorder | null;
  /**
   * The camera is reading the body confidently again (S2-6).
   *
   * The closing bracket of `recordInputUncertainty`, which only ever had an opening one. Raised when
   * the last capability that was unsure becomes sure — the moment "the camera could not read that"
   * stops being true — so a diagnostic can tell two separate unreadable attempts apart instead of
   * treating everything up to the next successful movement as one. It carries no player detail and
   * no confidence: it is the *end* of an episode, not an observation about a child.
   */
  onUncertaintyCleared?: (() => void) | undefined;
  /**
   * Every unreadable reading, **undeduplicated** (S3-3).
   *
   * The sibling of `analytics.recordInputUncertainty`, and deliberately not the same signal. The
   * session writer is deduplicated per capability per episode (D-062(4)) because a session record
   * should not carry one entry per frame — but a *diagnostic* has a different lifetime, and riding
   * on the deduplicated call meant an unreadable attempt that opened while the mission was held
   * swallowed the child's repeat after Resume. That is the attempt CAM-08 exists to measure, and
   * losing it made the recognised share falsely optimistic in exactly the situation a grown-up most
   * needs it to be honest.
   *
   * So: raw observation here, deduplicated record there, and whoever listens does its own grouping.
   * Carries no player id — it is an observation about what the camera could see, not about a child.
   */
  onUncertaintyObserved?:
    ((detail: { capability: InputUncertaintyCapability; confidence: number }) => void) | undefined;
  /**
   * A body this sitting is willing to call this hero's arrived in a frame (S3-6).
   *
   * The **attribution** boundary, not the confidence gate, and the distinction is the whole finding.
   * A frame reaching this adapter with a body in it is the resolver having decided whose body it is
   * (in a cooperative sitting) or there being only one child it could be (in a solo one). Whether
   * that body's *movements* can be read is a separate question this adapter answers frame by frame,
   * and the answer is often no — a dim room, a child side-on, a partial occlusion — which is exactly
   * the sitting a grown-up needs a camera-quality figure for.
   *
   * Reading "did this sitting ever have a body on camera" off the downstream `PLAYER_ENTERED_FRAME`
   * answered *no* for those sittings, so the summary dropped every uncertainty episode they
   * produced. Carries nothing: it is the fact that a body was read, not an observation about a child.
   */
  onBodyAttributed?: (() => void) | undefined;
  tunables?: GameplayTunables;
  /** The child's measurements. Slice C replaces the placeholder defaults with real ones. */
  calibration?: PoseCalibrationThresholds;
  /** Injected in tests; the default set is the five detectors a Spider Math mission needs. */
  detectors?: readonly PoseDetector[];
  /**
   * Which play space this sitting is in (GDS §6, D-059's parent choice).
   *
   * The *only* thing it changes here is which detector fills the count slot: Full-Motion counts
   * jumps, Compact counts the substitute. Everything downstream — the events, the matchers, the
   * runtime, the scoring — is identical, which is what "the learning mechanic remains counting"
   * (GDS §15) means in code.
   */
  cameraMode?: CameraMode;
  /** Which of GDS §15's three substitutes a Compact sitting counts with. Ignored in Full-Motion. */
  jumpSubstitute?: JumpSubstitute;
  /** The session clock. Defaults to `Date.now`, the same clock the keyboard stamps with. */
  now?: () => number;
  inputContext?: PoseInputContext;
  /**
   * Whether still-versus-moving is wired in. Off by default.
   *
   * The detector exists and is proved (MVP notes §17), but no shipped mechanic asks a child to hold
   * still, and every onset skips a narrative beat (R1-12) — so wiring PLAYER_MOVED into a camera
   * session would make every story moment vanish the instant a child shifted their weight. The
   * keyboard has no stillness key at all, so leaving it out is also the parity-preserving choice.
   */
  stillness?: boolean;
  /**
   * Whether this adapter is starting to watch a mission that is **already under way** (S5-2).
   *
   * A fresh sitting's first detector set observes its first posture as an onset, and that is the
   * pinned parity contract: attach a keyboard, press W, get `PLAYER_RAISED_HANDS` — a browser cannot
   * report a key that was already held, so there is nothing to be silent about.
   *
   * An adapter created into a mission already in flight is a different situation wearing the same
   * clothes. A child who leaves an incomplete camera mission and comes back is mid-round from the
   * first frame; if that frame finds them airborne or holding the Compact substitute, the landing
   * commits a repetition nobody watched begin, and one count is not the answer — so it settles as an
   * **incorrect academic attempt**, which is the locked "input-confidence failure is never an
   * academic failure" rule. Semantically this is the replacement-set case: a round is on screen, the
   * body has been moving unobserved, and the first posture has no history. So it re-acquires from
   * neutral, exactly as a set installed by `reconfigure()` does.
   */
  joiningMissionInProgress?: boolean;
}

/**
 * What a grown-up's mid-mission override may change (Slice G, GDS §32).
 *
 * Exactly two things, and both are numbers or a movement — never a rule. Restart calibration
 * replaces the measurements every detector reads; change jump substitute replaces the movement the
 * counting rounds are counted with. Everything else about the adapter is the same adapter.
 */
export interface CameraInputReconfiguration {
  /** Fresh measurements from a second run of hero training. */
  readonly calibration?: PoseCalibrationThresholds | undefined;
  /** Which of GDS §15's three substitutes a Compact sitting now counts with. */
  readonly jumpSubstitute?: JumpSubstitute | undefined;
}

export interface CameraInputAdapter {
  attach(): void;
  detach(): void;
  readonly attached: boolean;
  readonly playerId: PlayerId;
  /**
   * Whether a body is being read right now.
   *
   * Exposed for the recovery monitor, which has to know whether a body was **ever** acquired and
   * cannot learn it from the bus: in a camera sitting the keyboard adapter is attached too and
   * announces the same hero on attach, because a keyboard player is always there (CAM-09).
   */
  readonly present: boolean;
  readonly inputContext: PoseInputContext;
  /** Which play space this adapter was built for. Read by the diagnostics surfaces. */
  readonly cameraMode: CameraMode;
  /** Which substitute is in effect, or null in a Full-Motion sitting where jumps are the action. */
  readonly jumpSubstitute: JumpSubstitute | null;
  /** Switches what the body's centre means. Held postures are released under the old meaning first. */
  setInputContext(context: PoseInputContext): void;
  /**
   * Applies a grown-up's override (Slice G).
   *
   * Held postures are released under the *old* measurements first, exactly as a context switch does
   * (D-035): a body read against numbers that have just changed is a body nobody measured. The
   * round's own meaning (`inputContext`) is kept, because the mission has not moved.
   *
   * A no-op for an adapter that was handed its detectors directly (tests): there is nothing to
   * rebuild, and silently replacing an injected set would make a test about something else.
   */
  reconfigure(changes: CameraInputReconfiguration): void;
  /**
   * Discards whatever movement is part-way through, without saying anything (D-068(2), S1-3).
   *
   * Called when the mission stops listening — a grown-up's pause, a camera pause, the start of a
   * mid-mission re-training. It emits **nothing**: no onset, because nothing happened, and no
   * retraction, because a held posture is still held and the keyboard's own latches survive a pause
   * the same way (a physically held key produces no `keyup`). What it changes is that a jump that
   * was in the air, or an arm on its way down, is discarded rather than completed after Resume — on
   * the keyboard that action's `keydown` was consumed while the runtime was ignoring input, so a
   * camera that completed it would score something the keyboard could not.
   */
  neutralize(): void;
  /**
   * The camera has stopped producing frames altogether (`NO_FRAMES`, D-065(4)).
   *
   * The starvation watchdog runs on a wall clock outside the frame stream, so nothing here can
   * observe it — and until it reached the adapter, a child whose camera was unplugged mid-crouch
   * kept a held posture latched and was never reported as having left the frame. It takes the same
   * path as any other loss: retractions in the keyboard's order, then absence. **Never an onset.**
   */
  reportFramesStopped(): void;
  /**
   * Something knows this adapter was not shown the last stretch of time (P-A, S5-1).
   *
   * **Observation continuity is a fact about one lane**, and this adapter owns it. It decides for
   * itself from the timestamps of the frames it is actually handed — a hole longer than
   * `pose.observationGapMs` is time in which this body was not observed, solo and cooperative
   * alike — so nothing has to remember to tell it about a stalled tab, a machine that fell behind,
   * or a camera stopped and restarted underneath a screen that stayed subscribed.
   *
   * This is the door for the one thing timestamps cannot show: an interval in which frames *were*
   * arriving and this lane was nevertheless not being shown its own body. The cooperative pair
   * raises it on the edge into an unconfirmed attribution, where the frames are perfectly regular
   * and every one of them is being withheld from this lane while the resolver argues about whose
   * body it is (P-B). One frame of that is enough: a binary onset has neither the portal's dwell nor
   * the count's settle window in front of it, so a substitute body with its arms already up would
   * otherwise score on the first confirmed frame under the departed child's name.
   *
   * What it does, in order: retractions first, because they are always allowed and this adapter is
   * the only thing that knows what it has told the bus; then a neutral re-baseline of the semantic
   * state, so the first readable frame is adopted silently (I-2, D-069's family). Deliberately
   * **not** the loss path — no `PLAYER_LEFT_FRAME`, because nothing says the child went anywhere,
   * and no uncertainty record, because what happened is that nothing was shown rather than that this
   * child could not be read — and deliberately **not** the tracking gate's loss clock, which counts
   * how long since the body could be seen and is not refreshed by an interval nobody looked at
   * (S5-3).
   */
  reportObservationGap(): void;
  /**
   * This lane's body cannot be told apart from another one right now (Phase 4, D-076(1)).
   *
   * The adapter still knows nothing about lanes, second bodies or crossings — the lane resolver owns
   * "whose body is this" and nothing else may re-derive it (recon §B.2). What it is told is narrower
   * and is entirely about the hero it speaks for: *this lane is unreadable*.
   *
   * Its answer is the loss answer, minus the one claim it would be wrong to make. Held postures are
   * released in the keyboard's order and the detectors go back to neutral, so nothing in flight
   * completes and the first readable frame afterwards is a silent baseline (D-069's family rule).
   * **Never an onset**, exactly as tracking loss is never an onset. But no `PLAYER_LEFT_FRAME`: the
   * child has not left the picture, and saying they had would put a "come back" invitation in front
   * of a five-year-old who is standing right there.
   *
   * And it records what actually happened — the camera could not confidently observe this child —
   * so an ambiguous moment reaches the session as input uncertainty and never as a wrong answer.
   */
  reportIdentityUncertain(): void;
  /** Reads one frame. The tracker calls this; tests call it directly. */
  observe(frame: PoseFrame): void;
}

export function createCameraInputAdapter(options: CameraInputAdapterOptions): CameraInputAdapter {
  const {
    bus,
    playerId = HERO_1_PLAYER_ID,
    tunables = DEFAULT_GAMEPLAY_TUNABLES,
    calibration,
    analytics = null,
    tracker = null,
    now = () => Date.now(),
    cameraMode = DEFAULT_CAMERA_MODE,
    jumpSubstitute = DEFAULT_JUMP_SUBSTITUTE,
  } = options;

  /** The measurements and the counting movement in force right now — both a grown-up may change. */
  let activeCalibration = calibration;
  let activeSubstitute = jumpSubstitute;

  /**
   * Which arm last counted, kept for the sitting rather than for a detector instance.
   *
   * A grown-up's restart-calibration rebuilds the detector set, and a rebuilt detector remembers
   * nothing — so a parent who chose "alternating arm raises" would get the same arm counted twice
   * straight after re-measuring their child. The memory belongs to the room (D-068(2)).
   */
  const alternation = createCountAlternationMemo();

  const buildDetectors = (): readonly PoseDetector[] =>
    defaultDetectors({
      tunables,
      calibration: activeCalibration,
      stillness: options.stillness === true,
      cameraMode,
      jumpSubstitute: activeSubstitute,
      alternation,
    });

  const injectedDetectors = options.detectors ?? null;
  /**
   * The sitting's first detector set, and the one decision left in it (D-072(3), narrowed by S5-2).
   *
   * I-2 below is about *replacement* sets — sets that arrive mid-sitting under a body the adapter
   * has already been watching. A **fresh** sitting's first set has watched nobody, and the parity
   * contract (recon §B, `keyboard-camera-parity.test.ts`) is explicit that the first posture it
   * observes is an onset: on the keyboard, attaching and then pressing W emits
   * `PLAYER_RAISED_HANDS`, and a browser cannot report a key that was already held, so there is no
   * keyboard counterpart to be silent about. That case is kept, deliberately.
   *
   * What D-072(3) got wrong was its justification — "it reaches no round". A mission that is
   * **already under way** is mid-round from the first frame, and `joiningMissionInProgress` is how
   * the stage says so. Narrowed to the truth: a child already holding a posture on the very first
   * frame of a *fresh* sitting produces an onset, and that onset reaches no round, because a fresh
   * sitting has not presented one yet.
   */
  let detectors: readonly PoseDetector[] = injectedDetectors ?? buildDetectors();
  if (injectedDetectors === null && options.joiningMissionInProgress === true) {
    for (const detector of detectors) detector.reset();
  }

  /**
   * Installs a detector set — and there is exactly one way to do it, on purpose (S4-3).
   *
   * **I-2: every detector set this adapter installs begins with neutral re-acquisition.** The
   * D-068(2) family rule, stated over the *installation* rather than over `reset()`: a replacement
   * set has never seen this child, so it is in precisely the epistemic position of a set coming back
   * from an unobserved gap. The first readable frame is a silent baseline; a fresh onset takes a
   * genuinely new movement.
   *
   * `reconfigure()` used to reset the *outgoing* detectors and then throw them away, so the
   * replacements started cold — and because a grown-up's fresh measurements are applied
   * synchronously immediately before Resume, the first live frame of a posture the child was already
   * holding emitted an answer nobody gave. Routing both producers through here is what stops that
   * being a thing anybody has to remember.
   *
   * Injected sets (tests) are excluded, exactly as they are everywhere else: a test's stubs are its
   * own business, and silently resetting them would make a test about something else.
   */
  function installReplacementDetectors(next: readonly PoseDetector[]): void {
    detectors = next;
    resetDetectors();
  }

  let inputContext: PoseInputContext = options.inputContext ?? DEFAULT_POSE_INPUT_CONTEXT;
  let attached = false;
  let unsubscribe: (() => void) | null = null;

  /**
   * What the adapter believes about the child, and therefore what it owes the bus a retraction for.
   *
   * **Held postures only** — there is deliberately no lane here (S7-1). Which lane a body occupies
   * is the zone detector's to know: it already refuses to report the lane it believes the body is
   * in, so a copy of that rule in this file was the same statement made twice, and the two
   * statements drifted the moment one of them learned something in silence. A suppressed settle
   * moves the detector's belief and emits nothing, and this file's stale copy then dropped a
   * deliberate return to that lane as a duplicate of a movement the child had long since left.
   *
   * The keyboard's own lane latch is not a counter-example: there the adapter *is* the detector, so
   * there is still exactly one owner. That is the invariant — silence is about not emitting, never
   * about not knowing, and only one thing gets to know.
   */
  let present = false;
  let handsRaised = false;
  let crouched = false;
  /** Capabilities whose uncertainty has already been recorded for the episode they are in. */
  const uncertainCapabilities = new Set<InputUncertaintyCapability>();

  const emit = (event: ThriveInputEvent): void => {
    bus.emit(event);
  };

  function releaseHands(): void {
    if (!handsRaised) return;
    handsRaised = false;
    emit({ type: 'PLAYER_LOWERED_HANDS', playerId, at: now() });
  }

  function releaseCrouch(): void {
    if (!crouched) return;
    crouched = false;
    emit({ type: 'PLAYER_STOOD', playerId, at: now() });
  }

  /**
   * Everything the adapter believes is held, released in the keyboard adapter's order.
   *
   * The lane needs nothing here: there is no event for leaving one, and the detector that owns it is
   * reset by the same callers a moment later (S7-1).
   */
  function releaseHeld(): void {
    releaseHands();
    releaseCrouch();
  }

  function resetDetectors(): void {
    for (const detector of detectors) detector.reset();
  }

  /** The child is gone: retract, then say so. Never an onset (recon §B.4). */
  function handlePresenceLost(): void {
    releaseHeld();
    resetDetectors();
    if (!present) return;
    present = false;
    emit({ type: 'PLAYER_LEFT_FRAME', playerId, at: now() });
  }

  /** One detector lost its own part of the body: a blur, not a departure. */
  function handleCapabilityLost(capability: DetectorCapability): void {
    switch (capability) {
      case 'presence':
        handlePresenceLost();
        return;
      case 'hands':
        releaseHands();
        return;
      case 'crouch':
        releaseCrouch();
        return;
      default:
        // Zone, jump and stillness hold nothing the bus has been told about: there is no event for
        // leaving a lane, and the detector has already forgotten whatever it was part-way through.
        return;
    }
  }

  function recordUncertainty(capability: InputUncertaintyCapability, confidence: number): void {
    // Raw first, and always: see `onUncertaintyObserved` for why this is not the deduplicated call.
    options.onUncertaintyObserved?.({ capability, confidence });
    if (uncertainCapabilities.has(capability)) return;
    uncertainCapabilities.add(capability);
    analytics?.recordInputUncertainty(playerId, { capability, confidence });
  }

  /** One capability is sure again; if it was the last unsure one, the episode is over (S2-6). */
  function clearUncertainty(capability: InputUncertaintyCapability): void {
    if (!uncertainCapabilities.delete(capability)) return;
    if (uncertainCapabilities.size === 0) options.onUncertaintyCleared?.();
  }

  /** Applies one detector event, subject to the adapter's latches and the input context. */
  function applyEvent(event: DetectedInput): void {
    switch (event.type) {
      case 'PLAYER_ENTERED_FRAME':
        if (present) return;
        present = true;
        emit({ type: 'PLAYER_ENTERED_FRAME', playerId, at: now() });
        return;

      case 'PLAYER_ZONE_CHANGED': {
        // No centre-lane answer exists in a yes/no round: the keyboard's centre key is its crouch
        // key, so a centre reading there is the posture mechanic's business, not a lane.
        //
        // Note what is *not* here (S7-1): "re-entering the lane you are already in is not a new
        // answer" (recon §B.5) is real, and it is the zone detector's — it reports moves only. This
        // file second-guessing it is what let a suppressed settle desynchronise the two, and in a
        // yes/no round it went further: a child stepping left, back through the gated-out centre and
        // left again had their second, entirely deliberate entry dropped as a duplicate.
        if (inputContext === 'BINARY' && event.zone === 'center') return;
        emit({ type: 'PLAYER_ZONE_CHANGED', playerId, zone: event.zone, at: now() });
        return;
      }

      case 'PLAYER_JUMPED':
        /**
         * A yes/no round has nothing to count, and in Compact the counting action is performed
         * with the same arms that say "yes" (GDS §15). Without this, a child answering the
         * Spider-Sense Factory would also charge a web with the same movement. Full-Motion needs no
         * such rule — nobody says yes by jumping — but the gate is written for both so there is one
         * statement of when a count means anything, not two that could drift.
         */
        if (inputContext === 'BINARY' && cameraMode === 'compact') return;
        // Counted actions are never latched: four jumps are four answers.
        emit({ type: 'PLAYER_JUMPED', playerId, at: now() });
        return;

      case 'PLAYER_CROUCHED':
        if (inputContext !== 'BINARY') return;
        if (crouched) return;
        crouched = true;
        emit({ type: 'PLAYER_CROUCHED', playerId, at: now() });
        return;

      case 'PLAYER_STOOD':
        releaseCrouch();
        return;

      case 'PLAYER_RAISED_HANDS':
        /**
         * The other half of the Compact rule above, and the reason it is a rule rather than two.
         *
         * In a Compact sitting the arms carry both meanings: outside a yes/no round they are the
         * counting action, and inside one they are "yes". So each context keeps exactly one of
         * them — counts outside, an answer inside — and one movement never means two things at
         * once. This mirrors the keyboard's own context gating, where the crouch key is suppressed
         * in ZONES for the same reason (D-035/D-062(4)).
         */
        if (inputContext !== 'BINARY' && cameraMode === 'compact') return;
        if (handsRaised) return;
        handsRaised = true;
        emit({ type: 'PLAYER_RAISED_HANDS', playerId, at: now() });
        return;

      case 'PLAYER_LOWERED_HANDS':
        releaseHands();
        return;

      case 'PLAYER_BECAME_STILL':
        emit({ type: 'PLAYER_BECAME_STILL', playerId, at: now() });
        return;

      case 'PLAYER_MOVED':
        emit({ type: 'PLAYER_MOVED', playerId, at: now() });
        return;

      case 'PLAYER_LEFT_FRAME':
        // Absence is the adapter's to declare, from a `lost` outcome — never a detector's event.
        return;

      default:
        return;
    }
  }

  function applyOutcome(detector: PoseDetector, outcome: DetectorOutcome): void {
    switch (outcome.kind) {
      case 'none':
        clearUncertainty(detector.capability);
        return;
      case 'event':
        clearUncertainty(detector.capability);
        applyEvent(outcome.event);
        return;
      case 'uncertain':
        // No event, ever. This is the whole of PS3-1: below the gate, the session records that it
        // could not see, and the child's answer is simply not observed.
        recordUncertainty(detector.capability, outcome.confidence);
        return;
      case 'lost':
        handleCapabilityLost(detector.capability);
        return;
      default:
        return;
    }
  }

  /**
   * Whether this adapter is inside a run of frames it was actually shown (S5-5).
   *
   * The clock every posture claim in this file is measured against, and the shared one (D-090). Not
   * the tracking gate's clock: that counts how long since the body could be *read* and runs on
   * frames that arrive; this is about the frames that never came.
   */
  const continuity = createObservationContinuity(tunables);

  /**
   * The one thing an unobserved interval does, wherever it was noticed (S5-1/S5-3/S5-5).
   *
   * Retract under the meaning it was announced with, then re-baseline the semantic state. The
   * tracking gate's loss clock is untouched — separating those two is what lets this fire for a lane
   * whose child is already leaving without ever restarting the count towards saying so.
   */
  function breakObservation(): void {
    releaseHeld();
    for (const detector of detectors) {
      // `reacquireSilently` is the rule; `reset()` is the fallback for an injected stub in a test
      // that defines no opinion of its own.
      if (detector.reacquireSilently === undefined) detector.reset();
      else detector.reacquireSilently();
    }
  }

  function observe(frame: PoseFrame): void {
    /**
     * Was this body observed between the last frame and this one? (S5-5, the solo half of P-A.)
     *
     * Asked here, before anything is read, and asked by the thing that knows the answer: this
     * adapter is the only thing that knows which frames it was actually shown. A hole past the bound
     * — or a clock that went backwards, which is not a run of frames either — means whatever the
     * child did inside it was not watched, and the returning frame is a baseline rather than an
     * answer. The tracking gate cannot see it: a confident return takes its ready branch and reports
     * no loss however long the hole was, which is exactly why a 500 ms hole sat underneath
     * `trackingLossPauseMs` and produced an onset nobody watched begin.
     */
    if (continuity.note(frame.detectedAt)) breakObservation();

    // A frame reaching this adapter at all is the resolver saying it knows whose body this is, so
    // an identity episode ends here — the closing bracket of `reportIdentityUncertain`.
    clearUncertainty(IDENTITY_CAPABILITY);

    // …and a frame with a body in it is that decision having actually named somebody (S3-6). Before
    // the detectors run, deliberately: what happens next may well be "and we could not read them".
    if (frame.poses.length > 0) options.onBodyAttributed?.();

    for (const detector of detectors) {
      applyOutcome(detector, detector.observe(frame));

      // One guard, two jobs: nothing but presence is believed until somebody has been seen, and
      // once the body is gone the rest of this frame is meaningless — the readings describe a
      // child who is not there. (Presence runs first, so this is decided before anything else.)
      if (!present) return;
    }
  }

  return {
    get attached() {
      return attached;
    },

    playerId,

    cameraMode,

    get present() {
      return present;
    },

    get jumpSubstitute() {
      return cameraMode === 'compact' ? activeSubstitute : null;
    },

    get inputContext() {
      return inputContext;
    },

    reconfigure(changes: CameraInputReconfiguration): void {
      if (changes.calibration !== undefined) activeCalibration = changes.calibration;
      if (changes.jumpSubstitute !== undefined) activeSubstitute = changes.jumpSubstitute;
      if (injectedDetectors !== null) return;

      // Under the old numbers, before they stop being the numbers this posture was believed with.
      releaseHeld();
      // I-2: the replacements re-acquire from neutral, so a posture the child is *still* holding is
      // adopted as a baseline instead of announcing itself on the first frame after Resume (S4-3).
      installReplacementDetectors(buildDetectors());
    },

    neutralize(): void {
      /**
       * Only what is in flight (S2-3).
       *
       * A pause is not a break in observation — the frames keep arriving behind the overlay — so a
       * posture the child is still holding and the lane they are standing in are still true, and
       * re-baselining them would eat the answer they give the instant the mission comes back. What
       * is discarded is the jump between take-off and landing, the repetition between lift and
       * return, the lane change part-way through its debounce: on a keyboard the onset that began
       * those was consumed while input was being ignored, and no `keyup` can complete it afterwards.
       *
       * A detector with nothing in flight defines nothing and is left alone.
       */
      for (const detector of detectors) detector.discardInProgress?.();
    },

    reportFramesStopped(): void {
      handlePresenceLost();
      // The run of frames is over; the next one starts a run rather than continuing this one.
      continuity.forget();
    },

    reportObservationGap(): void {
      breakObservation();
    },

    reportIdentityUncertain(): void {
      /**
       * The loss path minus the departure, in that order and for the loss path's reasons.
       *
       * Retractions first, under the meaning they were announced with, because the adapter is the
       * only thing that knows what it has told the bus. Then the detectors go back to neutral —
       * which is what discards a jump that was in the air while nobody could say whose it was, and
       * what makes the first frame after the crossing a silent baseline instead of an answer the
       * child gave before it started (I-2, D-069's family).
       *
       * The confidence is 0 and means exactly that: this is not a weak reading of this child, it is
       * no reading of this child at all.
       */
      releaseHeld();
      resetDetectors();
      recordUncertainty(IDENTITY_CAPABILITY, 0);
    },

    setInputContext(context: PoseInputContext): void {
      /**
       * **This is the round-presentation boundary, and every presentation crosses it** (S7-2).
       *
       * The stage calls this on every `ROUND_PRESENTED`, so it is where a new question meets the
       * body already in front of the camera. Round 6 and earlier returned immediately when the
       * context had not changed, which made the boundary conditional on something it has nothing to
       * do with: a *mechanic change* got the neutralisation, and a **same-mechanic replacement** —
       * the simplified variant a timed-out question is replaced by — got nothing. A lane transition
       * already in flight for the old prompt then settled straight into the new matcher and
       * committed a wrong answer to a question the child never saw. Timeout is never wrong, and that
       * rule has to hold at the seam between two prompts as well as inside one.
       *
       * The two cases differ in *how much* is invalidated, not in whether the boundary exists:
       *
       *  - **the mechanic changed** — a crouch means something else now, so held postures are
       *    released under their old meaning and every detector re-acquires from neutral (D-035);
       *  - **the mechanic is the same** — what the child is *holding* is still true and still theirs
       *    to keep, so only what was **in flight** is discarded. That is the shipped pause-boundary
       *    rule (D-062(2)) and it is deliberately the narrow one: a lane nobody was walking out of
       *    stays exactly where it is, so a child who was standing still when the new question
       *    arrived and *then* steps has answered it, and is not made to step twice.
       */
      if (context === inputContext) {
        for (const detector of detectors) detector.discardInProgress?.();
        return;
      }
      // Release under the *old* meaning first, exactly as the keyboard does (D-035): after the
      // switch a crouch means something else, and the posture would never be retracted.
      releaseHeld();
      resetDetectors();
      inputContext = context;
    },

    observe(frame: PoseFrame): void {
      if (options.tracker !== undefined && options.tracker !== null && !attached) return;
      observe(frame);
    },

    attach(): void {
      if (attached) return;
      attached = true;
      // No PLAYER_ENTERED_FRAME here: unlike a keyboard, presence is something we observe.
      unsubscribe = tracker?.subscribe((frame) => observe(frame)) ?? null;
    },

    detach(): void {
      if (!attached) return;
      attached = false;
      unsubscribe?.();
      unsubscribe = null;
      // A departing adapter must not leave a posture latched behind it (D-048(2)).
      handlePresenceLost();
      continuity.forget();
      /**
       * …nor an unreadable attempt open behind it (S8).
       *
       * A lane released mid-uncertainty never becomes readable again, so the episode this adapter
       * opened had no other way to end. The flag it feeds is the *sitting's* — shared with the
       * sibling's lane in a cooperative room — and leaving it set deduplicates the sibling's next
       * genuine episode into nothing, making the CAM-08 count quietly too kind.
       */
      const wasUncertain = uncertainCapabilities.size > 0;
      uncertainCapabilities.clear();
      if (wasUncertain) options.onUncertaintyCleared?.();
    },
  };
}

function defaultDetectors(options: {
  tunables: GameplayTunables;
  calibration?: PoseCalibrationThresholds | undefined;
  stillness: boolean;
  cameraMode: CameraMode;
  jumpSubstitute: JumpSubstitute;
  alternation?: CountAlternationMemo | undefined;
}): PoseDetector[] {
  const detectorOptions = { tunables: options.tunables, calibration: options.calibration };

  /**
   * The count slot, filled by whichever action this room counts with (GDS §6/§15).
   *
   * A swap rather than an addition: a Compact sitting is one where the child is close enough that a
   * whole jump does not fit in frame, so keeping the jump detector alongside the substitute would
   * let a partly-visible hop count a charge nobody performed.
   */
  const countDetector =
    options.cameraMode === 'compact'
      ? createCountSubstituteDetector({
          ...detectorOptions,
          substitute: options.jumpSubstitute,
          alternation: options.alternation,
        })
      : createJumpDetector(detectorOptions);

  // Presence first: everything else is only meaningful once somebody is there.
  const detectors = [
    createPresenceDetector(detectorOptions),
    createZoneDetector(detectorOptions),
    countDetector,
    createCrouchDetector(detectorOptions),
    createHandsDetector(detectorOptions),
  ];

  if (options.stillness) detectors.push(createStillnessDetector(detectorOptions));
  return detectors;
}
